/**
 * Customer Portal - Booking Tracking and Management
 * Allows customers to track their bookings and manage their trips
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  XCircleIcon,
  PhotoIcon,
  StarIcon,
  HeartIcon,
  ShareIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Tour } from '../types';
import { tourService } from '../services/tourService';

interface CustomerBooking {
  id: string;
  bookingId: string;
  tourName: string;
  tourDate: string;
  participants: number;
  totalAmount: number;
  paidAmount: number;
  balanceDue: number;
  status: 'pending' | 'confirmed' | 'paid' | 'in_progress' | 'completed' | 'cancelled';
  paymentStatus: 'pending' | 'partial' | 'paid' | 'overdue';
  paymentMethod: string;
  specialRequests?: string;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  guideAssigned?: string;
  vehicleAssigned?: string;
  accommodationAssigned?: string;
  logisticsNotes?: string;
  nextSteps: string[];
  timeline: Array<{
    date: string;
    title: string;
    description: string;
    status: 'completed' | 'pending' | 'upcoming';
  }>;
}

const CustomerPortal: React.FC = () => {
  const { actualTheme } = useTheme();
  const { tourId } = useParams<{ tourId: string }>();
  const [bookings, setBookings] = useState<CustomerBooking[]>([]);
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<CustomerBooking | null>(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  // Load tour data and bookings
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load tour data if tourId is provided
        if (tourId) {
          const tourData = await tourService.getTourById(tourId);
          setTour(tourData);
        }

        // Mock bookings data - in real implementation, fetch from API based on customer ID
        const mockBookings: CustomerBooking[] = [
          {
            id: '1',
            bookingId: 'STS-001',
            tourName: 'Maasai Mara Safari',
            tourDate: '2024-02-15',
            participants: 2,
            totalAmount: 100000,
            paidAmount: 30000,
            balanceDue: 70000,
            status: 'confirmed',
            paymentStatus: 'partial',
            paymentMethod: 'mpesa',
            specialRequests: 'Vegetarian meals required',
            emergencyContact: {
              name: 'Jane Doe',
              phone: '+254 700 000 002',
              relationship: 'Spouse'
            },
            guideAssigned: 'Peter Kimani',
            vehicleAssigned: 'Toyota Land Cruiser - KDA 123A',
            accommodationAssigned: 'Mara Serena Lodge',
            logisticsNotes: 'Pickup from Jomo Kenyatta Airport at 6:00 AM',
            nextSteps: [
              'Complete remaining payment (KES 70,000)',
              'Receive pre-tour information package',
              'Confirm pickup details 24 hours before departure'
            ],
            timeline: [
              {
                date: '2024-01-15',
                title: 'Booking Confirmed',
                description: 'Your safari booking has been confirmed and payment of KES 30,000 received.',
                status: 'completed'
              },
              {
                date: '2024-01-20',
                title: 'Guide Assigned',
                description: 'Professional guide Peter Kimani has been assigned to your safari.',
                status: 'completed'
              },
              {
                date: '2024-02-01',
                title: 'Pre-Tour Information',
                description: 'Detailed information about your safari will be sent to your email.',
                status: 'pending'
              },
              {
                date: '2024-02-14',
                title: 'Final Confirmation',
                description: 'Final pickup details and logistics will be confirmed.',
                status: 'upcoming'
              },
              {
                date: '2024-02-15',
                title: 'Safari Departure',
                description: 'Your Maasai Mara Safari begins!',
                status: 'upcoming'
              }
            ]
          }
        ];

        setBookings(mockBookings);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [tourId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'confirmed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'in_progress': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getTimelineStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'upcoming': return 'bg-gray-300';
      default: return 'bg-gray-300';
    }
  };

  const handlePayment = (bookingId: string) => {
    // In real implementation, this would integrate with payment gateway
    console.log('Processing payment for booking:', bookingId);
    setShowPaymentModal(true);
  };

  const handleContactSupport = () => {
    setShowContactModal(true);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
        }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto mb-4"></div>
          <p className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
            }`}>Loading your safari details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className={`backdrop-blur-md shadow-modern rounded-2xl p-8 border transition-all duration-300 ${actualTheme === 'dark'
            ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
            : 'bg-white/95 border-neutral-200/50'
            }`}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className={`text-4xl font-bold transition-colors duration-300 ${actualTheme === 'dark'
                  ? 'text-slate-200'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent'
                  }`}>
                  {tour ? tour.title : 'My Safari Bookings'}
                </h1>
                <p className={`mt-2 text-lg transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  {tour ? tour.shortDescription : 'Track your adventures and manage your safari experiences'}
                </p>
              </div>
              <div className="hidden md:flex items-center space-x-4">
                <div className={`p-3 rounded-full transition-all duration-300 ${actualTheme === 'dark'
                  ? 'bg-blue-900/20'
                  : 'bg-blue-100'
                  }`}>
                  <UserGroupIcon className={`w-8 h-8 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>
                    {bookings.length}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                    }`}>
                    Active Bookings
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tour Details Section - Only show if tour is loaded */}
        {tour && (
          <div className="mb-8">
            <div className={`backdrop-blur-md shadow-modern rounded-2xl border transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
              : 'bg-white/95 border-neutral-200/50'
              }`}>
              {/* Navigation Tabs */}
              <div className="border-b border-gray-200 dark:border-slate-600">
                <nav className="flex space-x-8 px-6 pt-6">
                  {[
                    { id: 'overview', name: 'Overview', icon: DocumentTextIcon },
                    { id: 'itinerary', name: 'Itinerary', icon: CalendarDaysIcon },
                    { id: 'highlights', name: 'Highlights', icon: StarIcon },
                    { id: 'includes', name: 'What\'s Included', icon: CheckIcon },
                    { id: 'guide', name: 'Your Guide', icon: UserGroupIcon },
                    { id: 'location', name: 'Location', icon: MapPinIcon }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 hover-magnify-sm ${activeTab === tab.id
                        ? actualTheme === 'dark'
                          ? 'border-blue-400 text-blue-400'
                          : 'border-blue-600 text-blue-600'
                        : actualTheme === 'dark'
                          ? 'border-transparent text-slate-400 hover:text-slate-300'
                          : 'border-transparent text-neutral-500 hover:text-neutral-700'
                        }`}
                    >
                      <tab.icon className="w-5 h-5" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Tour Images */}
                    {tour.images && tour.images.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {tour.images.slice(0, 3).map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`${tour.title} - Image ${index + 1}`}
                              className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tour Description */}
                    <div>
                      <h3 className={`text-xl font-semibold mb-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>
                        About This Safari
                      </h3>
                      <p className={`text-lg leading-relaxed transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                        {tour.description}
                      </p>
                    </div>

                    {/* Tour Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                        }`}>
                        <div className="flex items-center">
                          <ClockIcon className={`w-5 h-5 mr-2 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`} />
                          <span className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            {tour.duration}
                          </span>
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                        }`}>
                        <div className="flex items-center">
                          <CurrencyDollarIcon className={`w-5 h-5 mr-2 ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`} />
                          <span className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            {tour.currency} {tour.price.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                        }`}>
                        <div className="flex items-center">
                          <UserGroupIcon className={`w-5 h-5 mr-2 ${actualTheme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                            }`} />
                          <span className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            Max {tour.maxParticipants}
                          </span>
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                        }`}>
                        <div className="flex items-center">
                          <StarIcon className={`w-5 h-5 mr-2 ${actualTheme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                            }`} />
                          <span className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            {tour.rating}/5
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'itinerary' && tour.itinerary && (
                  <div className="space-y-4">
                    <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>
                      Daily Itinerary
                    </h3>
                    {tour.itinerary.map((item, index) => (
                      <div key={index} className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                        }`}>
                        <div className="flex items-start">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${actualTheme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'
                            }`}>
                            <span className="text-white font-bold text-sm">{index + 1}</span>
                          </div>
                          <div>
                            <h4 className={`font-semibold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                              }`}>
                              {item.title}
                            </h4>
                            <p className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              {item.description}
                            </p>
                            {item.activities && item.activities.length > 0 && (
                              <div className={`text-sm mt-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                                }`}>
                                <span className="font-semibold">Activities: </span>
                                {item.activities.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'highlights' && tour.highlights && (
                  <div className="space-y-4">
                    <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>
                      Safari Highlights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {tour.highlights.map((highlight, index) => (
                        <div key={index} className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                          }`}>
                          <div className="flex items-center">
                            <CheckIcon className={`w-5 h-5 mr-3 ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                              }`} />
                            <span className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                              }`}>
                              {highlight}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'includes' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>
                        What's Included
                      </h3>
                      <div className="space-y-2">
                        {tour.includes && tour.includes.map((item, index) => (
                          <div key={index} className="flex items-center">
                            <CheckIcon className={`w-4 h-4 mr-3 ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                              }`} />
                            <span className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>
                        What's Not Included
                      </h3>
                      <div className="space-y-2">
                        {tour.excludes && tour.excludes.map((item, index) => (
                          <div key={index} className="flex items-center">
                            <XMarkIcon className={`w-4 h-4 mr-3 ${actualTheme === 'dark' ? 'text-red-400' : 'text-red-600'
                              }`} />
                            <span className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'guide' && tour.guides && tour.guides.length > 0 ? (
                  <div className="space-y-6">
                    {tour.guides.map((guide, index) => (
                      <div key={guide.id || index} className={`p-6 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                        }`}>
                        {index === 0 && (
                          <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            Your Professional Guide{tour.guides.length > 1 ? 's' : ''}
                          </h3>
                        )}
                        <div className="flex items-start space-x-4">
                          {guide.avatar && (
                            <img
                              src={guide.avatar}
                              alt={guide.firstName}
                              className="w-16 h-16 rounded-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                              }`}>
                              {guide.firstName} {guide.lastName}
                            </h4>
                            <p className={`mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              {guide.bio}
                            </p>
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center">
                                <StarIcon className={`w-4 h-4 mr-1 ${actualTheme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                                  }`} />
                                <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                                  }`}>
                                  {guide.rating}/5
                                </span>
                              </div>
                              <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                                }`}>
                                {guide.experience}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : activeTab === 'guide' && (
                  <div className={`p-8 rounded-lg transition-all duration-300 text-center border ${actualTheme === 'dark'
                    ? 'bg-amber-900/10 border-amber-500/20'
                    : 'bg-amber-50 border-amber-200'
                    }`}>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
                      <UserGroupIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className={`text-xl font-bold mb-2 ${actualTheme === 'dark' ? 'text-amber-400' : 'text-amber-800'
                      }`}>
                      This Tour Is Not Guided
                    </h3>
                    <p className={`max-w-md mx-auto leading-relaxed ${actualTheme === 'dark' ? 'text-amber-200/70' : 'text-amber-700'
                      }`}>
                      You are embarking on a self-guided experience. While you won't have a dedicated SightTrails guide, our support team remains fully available to assist you throughout your journey.
                    </p>
                  </div>
                )}

                {activeTab === 'location' && tour.location && (tour.location.name || tour.location.address) && (
                  <div className="space-y-4">
                    <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>
                      Safari Location
                    </h3>
                    <div className={`p-6 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                      }`}>
                      <h4 className={`text-lg font-semibold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>
                        {tour.location?.name || 'Unknown Location'}
                      </h4>
                      <p className={`mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                        {tour.location?.address || 'No address provided'}
                      </p>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <MapPinIcon className={`w-4 h-4 mr-2 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`} />
                          <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                            }`}>
                            {tour.location?.region || 'Unknown Region'}, {tour.location?.country || 'Unknown Country'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bookings List */}
        <div className="space-y-6">
          {bookings.map((booking) => (
            <div key={booking.id} className={`backdrop-blur-md shadow-modern rounded-2xl border transition-all duration-300 overflow-hidden ${actualTheme === 'dark'
              ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
              : 'bg-white/95 border-neutral-200/50'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className={`text-xl font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>
                      {booking.tourName}
                    </h2>
                    <p className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                      }`}>
                      Booking ID: {booking.bookingId} • {new Date(booking.tourDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full transition-all duration-300 ${getStatusColor(booking.status)}`}>
                      {booking.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {/* Payment Information */}
                  <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <h3 className={`font-semibold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Payment Status</h3>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                          }`}>Total:</span>
                        <span className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>KES {booking.totalAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                          }`}>Paid:</span>
                        <span className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                          }`}>KES {booking.paidAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                          }`}>Balance:</span>
                        <span className={`font-medium transition-colors duration-300 ${booking.balanceDue > 0
                          ? actualTheme === 'dark' ? 'text-red-400' : 'text-red-600'
                          : actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                          }`}>
                          KES {booking.balanceDue.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    {booking.balanceDue > 0 && (
                      <button
                        onClick={() => handlePayment(booking.id)}
                        className="w-full mt-3 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-300 hover-magnify-sm"
                      >
                        <CurrencyDollarIcon className="w-4 h-4 mr-2 inline" />
                        Pay Balance
                      </button>
                    )}
                  </div>

                  {/* Logistics Information */}
                  <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <h3 className={`font-semibold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Your Team</h3>
                    <div className="space-y-2">
                      {booking.guideAssigned && (
                        <div>
                          <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                            }`}>Guide:</span>
                          <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>{booking.guideAssigned}</div>
                        </div>
                      )}
                      {booking.vehicleAssigned && (
                        <div>
                          <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                            }`}>Vehicle:</span>
                          <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>{booking.vehicleAssigned}</div>
                        </div>
                      )}
                      {booking.accommodationAssigned && (
                        <div>
                          <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                            }`}>Accommodation:</span>
                          <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>{booking.accommodationAssigned}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className={`p-4 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <h3 className={`font-semibold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Next Steps</h3>
                    <ul className="space-y-1">
                      {booking.nextSteps.slice(0, 3).map((step, index) => (
                        <li key={index} className={`text-sm flex items-start transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                          }`}>
                          <span className={`w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0 transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-blue-400' : 'bg-blue-500'
                            }`}></span>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      setSelectedBooking(booking);
                      setShowBookingDetails(true);
                    }}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-300 hover-magnify-sm"
                  >
                    <DocumentTextIcon className="w-4 h-4 mr-2 inline" />
                    View Details
                  </button>
                  <button
                    onClick={handleContactSupport}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all duration-300 hover-magnify-sm"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2 inline" />
                    Contact Support
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all duration-300 hover-magnify-sm"
                  >
                    <DocumentTextIcon className="w-4 h-4 mr-2 inline" />
                    Print Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Booking Details Modal */}
        {showBookingDetails && selectedBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`backdrop-blur-md shadow-modern rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border border-slate-600/30'
              : 'bg-white/95 border border-neutral-200/50'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Booking Details - {selectedBooking.bookingId}</h2>
                  <button
                    onClick={() => setShowBookingDetails(false)}
                    className={`transition-colors duration-300 hover-magnify-sm ${actualTheme === 'dark'
                      ? 'text-slate-400 hover:text-slate-200'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Timeline */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">Booking Timeline</h3>
                  <div className="space-y-4">
                    {selectedBooking.timeline.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className={`w-4 h-4 rounded-full ${getTimelineStatusColor(item.status)} mt-1 mr-4 flex-shrink-0`}></div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-white">{item.title}</h4>
                              <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(item.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Emergency Contact</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <UserGroupIcon className="w-5 h-5 mr-2 text-gray-500" />
                        <span>{selectedBooking.emergencyContact.name}</span>
                      </div>
                      <div className="flex items-center">
                        <PhoneIcon className="w-5 h-5 mr-2 text-gray-500" />
                        <span>{selectedBooking.emergencyContact.phone}</span>
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedBooking.emergencyContact.relationship}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Support Contact</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <PhoneIcon className="w-5 h-5 mr-2 text-gray-500" />
                        <span>+254 708 560 665</span>
                      </div>
                      <div className="flex items-center">
                        <EnvelopeIcon className="w-5 h-5 mr-2 text-gray-500" />
                        <span>sighttrailsafaris@gmail.com</span>
                      </div>
                      <div className="flex items-center">
                        <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2 text-gray-500" />
                        <span>WhatsApp: +254 708 560 665</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Support Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`backdrop-blur-md shadow-modern rounded-2xl max-w-md w-full transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border border-slate-600/30'
              : 'bg-white/95 border border-neutral-200/50'
              }`}>
              <div className="p-6">
                <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Contact Support</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <PhoneIcon className={`w-5 h-5 mr-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-500'
                      }`} />
                    <div>
                      <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>Phone Support</div>
                      <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                        }`}>+254 708 560 665</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <EnvelopeIcon className={`w-5 h-5 mr-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-500'
                      }`} />
                    <div>
                      <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>Email Support</div>
                      <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                        }`}>sighttrailsafaris@gmail.com</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <ChatBubbleLeftRightIcon className={`w-5 h-5 mr-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-500'
                      }`} />
                    <div>
                      <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>WhatsApp</div>
                      <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                        }`}>+254 708 560 665</div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowContactModal(false)}
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all duration-300 hover-magnify-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPortal;
